import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import type {
  CpopoverAlign,
  CpopoverPlacement,
  PopoverPosition,
} from './Cpopover.types';
import {
  arePopoverPositionsEqual,
  computePopoverPosition,
} from './popover-position';

interface UsePopoverPositionInput {
  align: CpopoverAlign;
  anchorElement: HTMLElement | null;
  edgeMargin: number;
  estimatedHeight: number;
  gap: number;
  maxWidth: number;
  placement: CpopoverPlacement;
  visible: boolean;
}

interface UsePopoverPositionResult {
  popoverRef: RefObject<HTMLDivElement | null>;
  position: PopoverPosition | null;
}

function scheduleCancelableMicrotask(input: { callback: () => void }): () => void {
  let cancelled = false;

  queueMicrotask(() => {
    if (cancelled) {
      return;
    }

    input.callback();
  });

  return () => {
    cancelled = true;
  };
}

export function usePopoverPosition({
  align,
  anchorElement,
  edgeMargin,
  estimatedHeight,
  gap,
  maxWidth,
  placement,
  visible,
}: UsePopoverPositionInput): UsePopoverPositionResult {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  const updatePosition = useCallback((): void => {
    if (!visible || !anchorElement) {
      return;
    }

    const next = computePopoverPosition({
      anchorRect: anchorElement.getBoundingClientRect(),
      contentHeight: measuredHeight ?? estimatedHeight,
      placement,
      align,
      gap,
      edgeMargin,
      maxWidth,
    });

    setPosition((previous) =>
      arePopoverPositionsEqual({ previous, next }) ? previous : next,
    );
  }, [
    align,
    anchorElement,
    edgeMargin,
    estimatedHeight,
    gap,
    maxWidth,
    measuredHeight,
    placement,
    visible,
  ]);

  useLayoutEffect(() => {
    if (!visible || !anchorElement) {
      return;
    }

    return scheduleCancelableMicrotask({ callback: updatePosition });
  }, [visible, anchorElement, updatePosition]);

  useEffect(() => {
    if (visible) {
      return;
    }

    return scheduleCancelableMicrotask({
      callback: () => {
        setMeasuredHeight(null);
      },
    });
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const popover = popoverRef.current;
    if (!popover || typeof ResizeObserver !== 'function') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const nextHeight = entry.contentRect.height;
      setMeasuredHeight((previous) =>
        previous === nextHeight ? previous : nextHeight,
      );
    });

    observer.observe(popover);

    return () => {
      observer.disconnect();
    };
  }, [visible, position]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handlePositionUpdate = (): void => {
      updatePosition();
    };

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [visible, updatePosition]);

  return {
    popoverRef,
    position,
  };
}
